'use strict';

const BrewhouseService = require('services/BrewhouseServiceImpl'),
    ContentUtils = require('utils/ContentUtils'),
    {t} = require('services/i18nextWrapper'),
    NumberUtils = require('utils/NumberUtils'),
    DateUtils = require('utils/DateUtils'),
    ChronologycStore = require('stores/ChronologycStore'),
    TankStore = require('stores/TankStore'),
    moment = require('moment'),
    Constants = require('constants/AppConstants'),
    DashboardModalUtils = require('components/DashboardModal/DashboardModalUtils'),
    DashboardLayoutStore = require('stores/DashboardLayoutStore'),
    DashboardServiceCache = require('services/DashboardServiceCache'),
    Notification = require('components/Notification/Notification'),
    _ = require('lodash');

const BrewhouseStore = {

    fetchTask(success, failure) {
        BrewhouseService.fetchTask(success, failure);
    },

    addTask(content, success, failure) {
        BrewhouseService.addTask(content, success, failure);
    },

    changeTask(content, success, failure) {
        BrewhouseService.changeTask(content, success, failure);
    },

    deleteTask(id, success, failure) {
        BrewhouseService.deleteTask(id, success, failure);
    },

    getBatchPositions(order, offsetY = 0) {
        if (_.get(order, 'batchs.length', 0) === 0) {
            return []; // non-batch
        }

        const batchs = _.get(order, 'batchs');

        return _.map(batchs, batch => {
            let position = _.get(this.toBatchPosition(order, batch, offsetY), 'position');
            return position;
        });
    },

    toBatchPosition(order, batch, offsetY = 0, dashboardLayout = DashboardLayoutStore.getLayout()) {
        const turns = _.map(_.filter(DashboardServiceCache.getTanks(), tank => tank.type === 'BH'), turn => {
            turn.volume = +turn.volume;
            return turn;
        });
        const newPositionY = ChronologycStore.getYFromDate(batch.date) + offsetY;
        const newBatchDate = _.get(ChronologycStore.getDateFromPositionY(newPositionY), 'date');
        const positionId = `${_.get(order, 'id')}_BH|${_.get(batch, 'turn.column')}|${newBatchDate}`;
        const turn = _.find(turns, turn => _.get(batch, 'turnId') === turn.id);

        let position = {
            h: 1,                           // each batch have max 1 day
            x: _.get(turn, 'column'),       // use the same
            y: newPositionY,                // check existing batch with new Y position
            i: positionId,                  // use the same
            batchNumber: _.get(order, 'batchNumber'),  // use the same
            isRelocated: _.get(batch, 'position.isRelocated'),
            // commons fields
            static: true,
            verticalCompact: true,
            w: 1,
            isResizable: false,
            maxW: 1,
            minW: 1
        };

        const newBatch = {
            position,                                                             // not server persistence
            isAvailable: DashboardLayoutStore.isEmpty(position, dashboardLayout), // not server persistence
            turn: turn,                                                           // not server persistence
            turnId: _.get(turn, 'id'),                                            // will persist
            label: _.get(batch, 'label'),                                         // will persist
            description: _.get(batch, 'description'),                             // will persist
            task: _.get(batch, 'task'),                                           // will persist
            name: _.get(batch, 'task'),                                           // will persist
            date: newBatchDate,                                                   // will persist
            products: _.get(batch, 'products')                                    // will persist
        };

        // remove old batch of cloned order
       const filteredBatchs = _.filter(order.batchs, oldBatch => !(oldBatch.turnId === _.get(turn, 'id') && oldBatch.date === batch.date));
        //let filteredBatchs = _.filter(order.batchs, batch => batch.turnId !== _.get(turn, 'id'));
        // add new batch to replace old batch
        filteredBatchs.push(newBatch);
        // update batch with the position with order updated
        newBatch.position = position;

        return newBatch;
    },

    /**
     * Check if given order have an existing batchs. In case that already have batchs, we try to update only vertical
     * position (offset vertical position) of each existing batch if have space.
     * In case that don't exist batchs before, we assign a new one.
     * @param order
     * @param offset
     * @param turns
     * @param {Object} brewDate
     */
    getBatchAvailables(order, brewDate, offsetDays = 0, dashboardLayout = DashboardLayoutStore.getLayout(), isBatchingProcess = false) {

        const turns = _.map(_.filter(DashboardServiceCache.getTanks(), tank => tank.type === 'BH'), turn => {
            turn.volume = +turn.volume;
            return turn;
        });

        let resultBatchAvailables;

        if (_.get(order, 'batchs.length') > 0) {
            // enter here means that exist batch, and try to update to new position with the same turn only changing the date.

            const orderVolume = +order.volume;
            const resultRecalculatedExistingBatchs = this._recalculateExistingBatchs(order, dashboardLayout, offsetDays);

            if (resultRecalculatedExistingBatchs.volumeAvailable < orderVolume) {
                // enter means that the volume available is not enough, so we need to find the remaining volume needed to archive `orderVolume` target
                const batchNotAvailables = _.filter(resultRecalculatedExistingBatchs.existingBatchsUpdated, batch => !batch.isAvailable);
                const remainingVolume = (orderVolume - resultRecalculatedExistingBatchs.volumeAvailable);
                const newBatchAvailables = this._getNextBatchAvailable(order, turns, brewDate, remainingVolume, resultRecalculatedExistingBatchs.batchAvailables, dashboardLayout, isBatchingProcess);

                if (_.get(batchNotAvailables, 'length') > _.get(newBatchAvailables, 'length')) {
                    // enter here means usually means that between brewDate and packaginDate there is not batch available
                    // between that range of dates.
                    return null;
                }

                const newBatchAvailablesUpdated = this.migrateTaskFromOlderBatchNotAvailableToNewBatchAvailable(batchNotAvailables, newBatchAvailables);
                return _.flatten(_.concat(newBatchAvailablesUpdated, resultRecalculatedExistingBatchs.batchAvailables));
            }

            if (resultRecalculatedExistingBatchs.volumeAvailable >= orderVolume) {
                // enter here means that we have the batch with volume that we already needed. Finish here!
                resultBatchAvailables = resultRecalculatedExistingBatchs.batchAvailables;
            }
        } else {
            // Enter here means that the order is NEW, so just calculate the first batch available
            resultBatchAvailables = this._getNextBatchAvailable(order, turns, brewDate, order.volume, null, dashboardLayout, isBatchingProcess);
            if (!this.isEnoughBatchAvailable(+order.volume, resultBatchAvailables)) {
                // enter here means that the are not enough batch for the current range of date of the current order
                return null;
            }
        }

        if (!isBatchingProcess) {
            // enter here means that we need to revert re-located flag because is not executed this method from batching process
            resultBatchAvailables = _.map(resultBatchAvailables, batch => {
                batch = _.set(batch, 'position.isRelocated', false);
                return batch;
            });
        }

        return resultBatchAvailables;
    },

    isEnoughBatchAvailable(orderVolume, batchs) {
        const filteredBatchs = _.filter(batchs, batch => batch.isAvailable);
        const totalBatchVolume = _.map(filteredBatchs, batch => _.get(batch, 'turn.volume'), []).reduce((total, value) => total + value, 0);
        return totalBatchVolume >= orderVolume;
    },

    isEnoughVolumeInTank(order, columnTarget) {
        const orderTank = TankStore.getTankByColumn(columnTarget);
        return +orderTank.volume >= +_.get(order, 'volume');
    },

    _recalculateExistingBatchs(order, layout, offsetDays) {
        let existingBatchs = _.get(order, 'batchs');
        let volumenTargetCount = 0;

        const existingBatchsUpdated = _.map(existingBatchs, batch => {
            let newBatch = this.toBatchPosition(order, batch, offsetDays, layout);
            volumenTargetCount += +_.get(newBatch, 'turn.volume', 0) || 0;
            if (newBatch.isAvailable) {
                newBatch = _.set(newBatch, 'position.isRelocated', true);
            }
            return newBatch;
        });

        // search if some of batch is not available with the new vertical position.
        const batchAvailables = _.filter(existingBatchsUpdated, batch => batch.isAvailable);
        const volumeAvailable = this._calculateTotalVolumeOfBatchs(batchAvailables);

        return {
            batchAvailables: batchAvailables,
            volumeAvailable: volumeAvailable,
            existingBatchsUpdated
        };
    },

    migrateTaskFromOlderBatchNotAvailableToNewBatchAvailable(oldBatchNotAvailable, newBatchAvailable) {
        // ask if we have old task to migrate to new available task
        if (_.get(oldBatchNotAvailable, 'length', 0) === 0) {
            return newBatchAvailable;
        }
        // migrate old task to new batch available
        return _.map(newBatchAvailable, (newBatch, indexBatch) => {
            const oldBatch = oldBatchNotAvailable[indexBatch];
            if (oldBatch) {
                newBatch.task = _.get(oldBatch, 'task', '');
                newBatch.label = _.get(oldBatch, 'label', '');
                newBatch.description = _.get(oldBatch, 'description', '');
                newBatch.products = _.get(oldBatch, 'products', '');
            }
            return newBatch;
        });
    },

    _calculateTotalVolumeOfBatchs(batchs = []) {
        const turnsVolume = _.map(batchs, batch => +_.get(batch, 'turn.volume'));
        return _.reduce(turnsVolume, (total, volume) => total + volume, 0);
    },

    _getNextBatchAvailable(order, turns, brewDate, targetVolume = Number.MAX_SAFE_INTEGER, batchOrderAlreadyAvailables, dashboardLayout = DashboardLayoutStore.getLayout()) { // default null max integer, in order to search all batch within 3 days
        let daysBeetweenDates,
            firstDate,
            indexDay = -1,
            batchAvailables = [],
            volumenTargetCount = 0;

        if (order.isSingle) {
            daysBeetweenDates = DateUtils.getDaysBetween(order.fromDate, order.toDate);
            firstDate = order.fromDate;
        } else {
            daysBeetweenDates = DateUtils.getDaysBetween(brewDate, order.filtrationDate);
            firstDate = brewDate;
        }

        const brewDateY = ChronologycStore.getYFromDate(firstDate);
        const MAX_DAYS_TO_CALCULATE_BREWHOUSE = daysBeetweenDates;

        targetVolume = +targetVolume; // cast to number.
        let allBatchFounded = false;

        // first calculate all availability of each batch in the new position
        while (indexDay < MAX_DAYS_TO_CALCULATE_BREWHOUSE) {
            if (allBatchFounded) {
                break;
            }
            indexDay++;
            const batchDateY = brewDateY + indexDay;
            const batchDate = _.get(ChronologycStore.getDateFromPositionY(batchDateY), 'date');

            // iterate each turn of day in order to make batch validation
            for (const turn of turns) {

                const positionId = `${_.get(order, 'id')}_BH|${_.get(turn, 'column')}|${batchDate}`;
                const position = {
                    batchNumber: _.get(order, 'batchNumber'),
                    h: 1, // each batch have max 1 day
                    x: turn.column,
                    y: batchDateY,
                    i: positionId,
                    isRelocated: true
                };

                // ask if this `position` is not already in use for the current order
                if (_.some(batchOrderAlreadyAvailables, batchAlreadyTaken => _.get(batchAlreadyTaken, 'position.x') === position.x && _.get(batchAlreadyTaken, 'position.y') === position.y)) {
                    continue;
                }

                if (DashboardLayoutStore.isEmpty(position, dashboardLayout)) {
                    batchAvailables.push({
                        position,                            // not server persistence
                        isAvailable: true,                   // not server persistence
                        turn,                                // not server persistence
                        isRelocated: true,                   // not server persistence
                        turnId: turn.id,                     // will persist
                        date: batchDate                      // will persist
                    });
                    volumenTargetCount += (+turn.volume || 0);
                }

                if (volumenTargetCount >= targetVolume) {
                    allBatchFounded = true;
                    return batchAvailables;
                }
            }
        }

        return batchAvailables;
    },

    recalculateBrewhouseBatchsWithLayout(order, offsetDays, layout = DashboardLayoutStore.getLayout(), isBatchingProcess = false) {
        return this.recalculateBrewhouseBatchs(order, offsetDays, false, order.styleBeer, layout, isBatchingProcess);
    },

    /**
     * Calculate all brewhouse batch needed for given order.
     * @param {Object} order
     * @param {Number} offsetDays
     * @param {Boolean} forceToUpdateBatchAnyway
     * @return {*}
     */
    recalculateBrewhouseBatchs(order, offsetDays, forceToUpdateBatchAnyway, beerStyleSelected, layout = DashboardLayoutStore.getLayout(), isBatchingProcess = false) {
        const orderVolume = ContentUtils.getNumber(_.get(order, 'volume'));

        if (orderVolume === 0) {
            // set zero which means the tab should be visible but just empty, otherwhise if batchs are undefiend
            // the tab will not show
            return _.set(order, 'batchs', []);
        }

        if (order && orderVolume) {
            // already prepare for single mode.
            let brewDate;

            if (order.isSingle) {
                brewDate = _.get(order, 'fromDate');
            } else {
                brewDate = _.get(order, 'brewDate');
            }
            const targetVolume = ContentUtils.getNumber(order.volume);

            // starting flag to be use to indicate when all of batch wanted (which means target volume archived) are already founded
            order.allBatchLocated = false;

            // return the batch availables needed for the current order.
            let batchAvailables = this.getBatchAvailables(order, brewDate, offsetDays, layout, isBatchingProcess);

            if (!batchAvailables) {
                // enter here means there not have enough tanks for the volume order or just all turns are already occupied
                order.allBatchLocated = false;
                order.brewhouseMessageError = t('brewhouse_store_message_error_do_not_have_enough_turns_available');
                return order;
            } else {
                order.brewhouseMessageError = undefined;
            }

            if (!this.isBrewdateAvailable(batchAvailables, brewDate)) {
                // enter here means that don't exist batch for brewDate, so could not possible to change the order
                order.allBatchLocated = false;
                order.brewhouseMessageError = t('notification_brewhouse_at_full_capacity_on_selected_date');
                return order;
            }

            let batchFiltered = this._filterOnlyBatchNeeded(batchAvailables, orderVolume);
            const totalBatchOrder = this._calculateTotalVolumeBatch(batchFiltered);
            batchFiltered = DashboardModalUtils.hydrateBatchWithProcess(batchFiltered, order.batchs);

            if (totalBatchOrder >= targetVolume) {
                order.allBatchLocated = true;
                order.brewhouseMessageError = undefined;
                // apply configuration beer style of brewhouse section
                order.batchs = this._applyBrewhouseConfigurationTask(batchFiltered, beerStyleSelected, order.batchs);
                // update the endDate of each product of the batchs
                order = this._recalculateBrewingProductEndDate(order, offsetDays);
                return order;
            } else {
                order.allBatchLocated = false;
                order.brewhouseMessageError = '';
                if (forceToUpdateBatchAnyway) {
                    order.batchs = batchFiltered;
                }
                return order;
            }
        } else {
            // remove batch older, just in case that have batch before
            order.batchs = null;
        }

        return order;
    },

    _recalculateBrewingProductEndDate(order, offsetDays) {
        order.batchs = _.map(order.batchs, task => _recalculateOffsetProductsEndDate(task, offsetDays));
        return order;
    },

    _applyBrewhouseConfigurationTask(batchs, currentBeerStyle, existingBatchs) {
        if (batchs && currentBeerStyle) {
            let brewingTaskConfiguration = _.filter(DashboardServiceCache.getBeerStyleTaskConfiguration(), task => {
                return task.styleBeerId === +currentBeerStyle.id && task.typeTank === Constants.BREWING_TYPE_TASK;
            });

            if (_.get(brewingTaskConfiguration, 'length', 0) > 0) {
                // apply configuration of each batch
                const sortedBatchs = DateUtils.sortListByDateField(batchs, 'date');

                brewingTaskConfiguration.forEach((configBatch, index) => {
                    let batch = sortedBatchs[index];
                    if (batch) {
                        const existingBatch = existingBatchs.find(batchOld => batchOld.turnId === batch.turnId);
                        batch.task = batch.task || configBatch.typeTask;
                        batch.name = batch.name || isEmpty(batch.name) ? batch.name : configBatch.typeTask;
                        batch.label = batch.label || isEmpty(batch.label) ? batch.label : configBatch.label;
                        batch.description = batch.description || isEmpty(batch.description) ? batch.description :configBatch.description;
                        batch.isConfiguration = true;
                        if (_.get(existingBatch, 'process.length', 0) > 0)  {
                            batch.process = existingBatch.process;
                        }
                    }
                });
                return DateUtils.sortListByDateField(sortedBatchs, 'date');
            }
        }
        return batchs;
    },


    /**
     * Ask if exist some batch on brew date or one day before of given brew date.
     * @param {Array} batchs
     * @param {String} brewdate
     * @return {*}
     */
    isBrewdateAvailable(batchs, brewdate) {
        return _.some(batchs, batch => batch.date === brewdate) ||
                _.some(batchs, batch => batch.date === DateUtils.addDaysTextBasicFormat(brewdate, -1));
    },

    /**
     * Clean all information about brewhouse in given state.
     * @param nextState
     * @return {*}
     */
    cleanBrewhouse(nextState, defaultValue) {
        nextState = _.set(nextState, 'order.batchs', defaultValue);
        nextState = _.set(nextState, 'batchs', defaultValue);
        return nextState;
    },

    /**
     * Validate if the column that try to take is enough at level of volume of tank.
     * @param {Object} position         that will be validated if is fit to the column that try to fill.
     * @param {integer} volume          The volume of the order that will be validated
     * @return {Boolean}
     */
    isProperlyVolumeOfTankDestination(position, volume) {
        const tankToValidate = _.find(DashboardServiceCache.getTanks(), tank => tank.column === position.x);

        if (tankToValidate) {
            return (+tankToValidate.volume) >= +volume;
        } else {
            Notification.show(t('notification_column_of_the_new_position') + position.x + t(' notification_do_not_exist'), 'error');
            return false;
        }
    },

    calculateBatchVolume(preDefinedOrder, batchNumber, layout = DashboardLayoutStore.getLayout()) {
        if (!batchNumber) {
            batchNumber = String(_.get(preDefinedOrder, 'orderSelected.batchNumber'));
        }
        // filter orders by given bach number
        const batchOrders = _.filter(layout, (position => String(position.batchNumber) === batchNumber));
        // calculate FV total batch volume
        const fermentingVolume = _calculateSectionVolume(batchOrders, item => !ContentUtils.isBH(item.i) && !ContentUtils.isBT(item.i) && !ContentUtils.isBHBatch(item.i));
        // calculate BT total batch volume
        const packagingVolume = _calculateSectionVolume(batchOrders, item => ContentUtils.isBT(item.i));
        // get the max section volume
        const totalBatchVolume = _.max([fermentingVolume, packagingVolume]);

        preDefinedOrder.packagingVolume = packagingVolume;
        preDefinedOrder.fermentingVolume = fermentingVolume;
        preDefinedOrder.totalBatchVolume = totalBatchVolume;
        preDefinedOrder.totalBatchVolumeMessageError = this.getLabelVolumeBySections(fermentingVolume, packagingVolume);

        return preDefinedOrder;
    },
    
    getTaskFromOrder(order) {
        if (!order) {
            return order;
        }
        if (_.get(order, 'isSingle')) {
            return _.filter(_.concat(order.singleTasks, order.batchs), task => task);
        } else {
            return _.filter(_.concat(order.batchs, order.fermenterTasks, order.packagingTasks), task => task);
        }
    },
    
    getAllProductsFromOrder(order) {
        const tasks = this.getTaskFromOrder(order);
        return _.flatMap(tasks, task => _.get(task, 'products') || []);
    },
    
    getAllItemsFromAllProductsFromOrder(order) {
        const items = [];
            const products = this.getAllProductsFromOrder(order);
            for (const product of products) {
                items.push(product.items);
            }
        return _.flatMap(items);
    },
    
    calculateCostByOrder(order, vendorItems) {
        const allItems = this.getAllItemsFromAllProductsFromOrder(order);
        return this.calculateCostByItems(allItems, vendorItems);
    },
    
    calculateCostByProduct(product, vendorItems) {
        return this.calculateCostByItems(product.items, vendorItems);
    },
    
    calculateCostByItems(allItems, vendorItems) {
        // we should not calculate in batch cost the item Addition, because that product doesn't
        // is created from the final product, so it doesn't count in the cost.
        const allItemsWithoutAdditions = allItems.filter(item => !item.addition);
        return allItemsWithoutAdditions.reduce((total, item) => {
            const vendorItem = vendorItems.find(vendorItem => vendorItem.itemId === item.itemId);
            const priceUnit = vendorItem ? vendorItem.priceUnit || '1' : '1';
            const relativeUnitAmount = vendorItem ? vendorItem.relativeUnitAmount || '1' : '1';
            const value = NumberUtils.divide(priceUnit, relativeUnitAmount);
            const subTotal = NumberUtils.multiply(value, item.amount);
            total = NumberUtils.sum(subTotal, total);
            total = NumberUtils.formatDecimal(total);
            return total;
        }, 0);
    },

    calculateBatchVolumeByBatchNumber(batchNumber, orderIdExclusive, layout = DashboardLayoutStore.getLayout()) {
        let result = {};
        let batchOrders;
        // filter orders by given bach number
        if (orderIdExclusive) {
            batchOrders = _.filter(layout, (position => String(position.batchNumber) === batchNumber && ContentUtils.cleanId(position.i) === orderIdExclusive));
        } else {
            batchOrders = _.filter(layout, (position => String(position.batchNumber) === batchNumber));
        }
        // calculate FV total batch volume
        const fermentingVolume = _calculateSectionVolume(batchOrders, item => !ContentUtils.isBH(item.i) && !ContentUtils.isBT(item.i) && !ContentUtils.isBHBatch(item.i));
        // calculate BT total batch volume
        const packagingVolume = _calculateSectionVolume(batchOrders, item => ContentUtils.isBT(item.i));
        // get the max section volume
        const totalBatchVolume = _.max([fermentingVolume, packagingVolume]);

        result.packagingVolume = NumberUtils.getNumber(packagingVolume);
        result.fermentingVolume = NumberUtils.getNumber(fermentingVolume);
        result.totalBatchVolume = NumberUtils.getNumber(totalBatchVolume);
        return result;
    },


    isSomeBatchChange(oldBatchs, newBatchs) {
        if (_.get(oldBatchs, 'length', 0) > 0 && _.get(newBatchs, 'length', 0) > 0) {
            for (const oldBatch of oldBatchs) {
                const newBatch = _.find(newBatchs, batch => batch.i === oldBatch.i);
                if (!ContentUtils.isSameObject(oldBatch, newBatch, ['date', 'description', 'label', 'task'])) {
                    return false;
                }
            }
        }
        return true;
    },

    getLabelVolumeBySections(fermentingVolume, packagingVolume) {
        return `Fermenting (${fermentingVolume}) Packaging (${packagingVolume})`;
    },

    formatBatchVolumeLeft(batchVolume) {
        const unit = DashboardServiceCache.getCurrentUnitTank();
        //return `${NumberUtils.formatDecimal(batchVolume)}${unit} (${NumberUtils.formatDecimal(leftVolume)}${unit} left)`;
        return `${NumberUtils.formatDecimal(batchVolume)}${unit}`;

    },

    /**
     * Given batchs retrieve the total of volume of all batchs.
     * @param batchs
     * @return {*}
     * @private
     */
    _calculateTotalVolumeBatch(batchs, volumeField = 'turn.volume') {
        return _.reduce(_.map(batchs, batch => +_.get(batch, volumeField)), (total, batchVolume) => total + batchVolume, 0);
    },

    _filterOnlyBatchNeeded(batchs = [], targetVolume = 0) {
        let targetBatchs = [];
        let countVolume = 0;
        for (const batch of batchs) {
            const batchVolume = +_.get(batch, 'turn.volume');
            countVolume += batchVolume;
            if (countVolume < targetVolume) {
                targetBatchs.push(batch);
            } else if (countVolume >= targetVolume) {
                targetBatchs.push(batch);
                return targetBatchs;
            }
        }
        return targetBatchs;
    },

    updateTankSelectionBrewhouseElegible(dateClickedFrom) {
        let nextState = {};
        const dateClickedTo = DateUtils.addDays(dateClickedFrom.date, 14); // make 14, like default when open dialog new before select beer style!
        const tankTypeTarget = 'FV'; // we search the first available from FV to simulate clicking on FV available position.
        const tankAvailability = TankStore.getAvailabilityTanks(dateClickedFrom.date, dateClickedTo, tankTypeTarget);
        const availableTanks = _.filter(tankAvailability, tank => tank.isAvailable);
        const tankElegible = _.first(availableTanks); // select the first available from FV
        const tankElegibleIndex = _.findIndex(availableTanks, tank => tank.id === _.get(tankElegible, 'id')); // select the first available from FV
        nextState.tankSelected = tankElegible;
        nextState.singleTank = tankElegible;
        nextState.singleTankIndex = tankElegibleIndex;
        nextState.orderDateClicked = dateClickedFrom.date;
        // Assign the volume of turn instead of
        // first fermenting tank eligible
        nextState = _.set(nextState, 'tankSelected.volume', _.get(dateClickedFrom, 'tankSelected.volume'));
        return nextState;
    }

};

function _calculateSectionVolume(batchs, filterBatch) {
    const batchsFiltered = _.filter(batchs, filterBatch);
    const volumeBatchs = _.map(batchsFiltered, item => {
        if (item && item.volume || item.volume === 0) {
            return ContentUtils.getNumber(item.volume);
        }
        const order = DashboardServiceCache.getSafeOrder(_.get(item, 'i'));
        if (!order) {
            return 0;
        }
        const volumeOrder = +order.volume;
        return volumeOrder;
    });

    const totalVolume = _.reduce(volumeBatchs, (totalVolume, volume) => totalVolume + volume, 0);
    return totalVolume || 0;
}

function _recalculateOffsetProductsEndDate(task, offesetDays) {
    const products = _.get(task, 'products');

    if (ContentUtils.isNotEmptyArray(products)) {
        const productsUpdated = _.map(products, product => {
            product.endDate = moment(product.endDate || task.date).add(offesetDays, 'days').format(Constants.DATE_FORMAT_LONG);
            return product;
        });
        task.products = productsUpdated;
    }

    return task;
}

function isEmpty(value) {
    return '' === value;
}

module.exports = BrewhouseStore;



/** WEBPACK FOOTER **
 ** ./src/stores/BrewhouseStore.js
 **/